import React from 'react';

import { AlertCircle, CheckCircle2, CreditCard, Plus } from 'lucide-react';

import { useMultiplePayment } from '../../../../hooks/sale/useMultiplePayment';

interface Props {
  onOpenModal: () => void;
}

const formatCurrency = (value?: number) =>
  typeof value === 'number' ? `$${value.toFixed(2)}` : '$0.00';

const POSMultiplePaymentSection: React.FC<Props> = ({ onOpenModal }) => {
  // =====================================================
  // 🔹 Hook
  // =====================================================
  const { totalEntered, pendingAmount } = useMultiplePayment();

  // =====================================================
  // 🔹 States
  // =====================================================
  const isPending = (pendingAmount ?? 0) > 0;

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <div
      className="
        rounded-lg
        border
        border-stroke

        bg-white

        p-4

        shadow-default

        dark:border-strokedark
        dark:bg-boxdark
      "
    >
      {/* ===================================================== */}
      {/* 🔹 Header */}
      {/* ===================================================== */}
      <div className="flex items-start gap-3">
        {/* 🔹 Icon */}
        <div
          className="
            flex
            h-10
            w-10
            shrink-0
            items-center
            justify-center

            rounded-lg

            bg-primary/10

            text-primary
          "
        >
          <CreditCard size={18} />
        </div>

        {/* 🔹 Info */}
        <div className="min-w-0 flex-1">
          <h4
            className="
              text-sm
              font-semibold
              text-black

              dark:text-white
            "
          >
            Pagos Múltiples
          </h4>

          <p
            className="
              mt-1
              text-xs
              leading-relaxed
              text-body
            "
          >
            Divide el pago entre múltiples métodos de pago.
          </p>
        </div>
      </div>

      {/* ===================================================== */}
      {/* 🔹 Summary */}
      {/* ===================================================== */}
      <div className="mt-4 space-y-3">
        {/* 🔹 Entered */}
        <div
          className="
            flex
            items-center
            justify-between

            rounded-lg
            border
            border-stroke

            bg-whiten

            px-3
            py-2

            dark:border-strokedark
            dark:bg-meta-4
          "
        >
          <div className="flex items-center gap-2">
            <CheckCircle2
              className="
                h-4
                w-4

                text-[#3CA745]
              "
            />

            <span
              className="
                text-sm
                text-body
              "
            >
              Ingresado
            </span>
          </div>

          <span
            className="
              text-sm
              font-semibold
              text-[#3CA745]
            "
          >
            {formatCurrency(totalEntered)}
          </span>
        </div>

        {/* 🔹 Pending */}
        <div
          className="
            flex
            items-center
            justify-between

            rounded-lg
            border
            border-stroke

            bg-whiten

            px-3
            py-2

            dark:border-strokedark
            dark:bg-meta-4
          "
        >
          <div className="flex items-center gap-2">
            <AlertCircle
              className={`
                h-4
                w-4

                ${isPending ? 'text-[#DC3545]' : 'text-[#3CA745]'}
              `}
            />

            <span
              className="
                text-sm
                text-body
              "
            >
              Pendiente
            </span>
          </div>

          <span
            className={`
              text-sm
              font-semibold

              ${isPending ? 'text-[#DC3545]' : 'text-[#3CA745]'}
            `}
          >
            {formatCurrency(pendingAmount)}
          </span>
        </div>
      </div>

      {/* ===================================================== */}
      {/* 🔹 Action */}
      {/* ===================================================== */}
      <button
        type="button"
        onClick={onOpenModal}
        className="
          mt-4

          flex
          w-full
          items-center
          justify-center
          gap-2

          rounded-lg

          bg-primary

          px-4
          py-3

          text-sm
          font-medium
          text-white

          transition-all

          hover:bg-opacity-90
        "
      >
        <Plus size={16} />
        Configurar Pagos
      </button>
    </div>
  );
};

export default POSMultiplePaymentSection;
